import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DatePickerNew, TextArea } from 'common-src/components/base';
import {
  createAwscChatMessage,
  getAwscChatMessage,
  updateAwscChatMessage,
} from 'common-src/models/AwscChatMessage';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import useCustomSelector from 'src/hooks/useCustomSelector';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import { initialState } from './constants';
import { getIsButtonEnabled, validateScheduleTime } from './helpers';
import styles from './ScheduledMessagePopup.module.scss';

const ScheduledMessagePopup = ({ id, patientId, phoneNumber, onClose }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState(initialState);

  const patient = useCustomSelector((state) => singleModelSelector(state, 'Patient', patientId));
  const awscChatMessage = useCustomSelector((state) => getAwscChatMessage(state, id));

  const { errors, setErrors, renderButtons } = usePopup();

  useEffect(() => {
    if (!awscChatMessage) return;

    setData({
      message: awscChatMessage.message,
      scheduledAt: moment(awscChatMessage.scheduledAt)
        .tz(patient.timeZone)
        .format('YYYY-MM-DD HH:mm'),
    });
  }, [awscChatMessage]);

  const sendSmsRequest = (loadingCallback, successCallback, errorCallback) => {
    loadingCallback();

    const scheduledAt = moment
      .tz(data.scheduledAt, 'YYYY-MM-DD HH:mm', patient.timeZone)
      .utc()
      .format();

    if (id) {
      dispatch(
        updateAwscChatMessage(
          id,
          { message: data.message, scheduledAt },
          {
            successBlock: () => {
              successCallback('Scheduled message updated!');
              onClose(true);
            },
            errorBlock: (error) => errorCallback(error),
          },
        ),
      );
      return;
    }

    dispatch(
      createAwscChatMessage(
        {
          patientId,
          outboundPhoneNumber: phoneNumber,
          message: data.message,
          scheduledAt,
        },
        {
          successBlock: () => {
            successCallback('Message scheduled!');
            onClose(true);
          },
          errorBlock: (error) => errorCallback(error),
        },
      ),
    );
  };

  const onTextChange = (message) => {
    setData({ ...data, message });
    setErrors((prev) => ({ ...prev, message: !message ? 'Message should not be empty' : '' }));
  };

  const onDateChange = (date) => {
    if (date === undefined) {
      setErrors((prev) => ({ ...prev, scheduledTime: 'Invalid date format' }));
      return;
    }

    const scheduledAt = date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : null;
    setErrors((prev) => ({ ...prev, ...validateScheduleTime(scheduledAt, patient.timeZone) }));
    setData((prev) => ({ ...prev, scheduledAt }));
  };

  return (
    <BasePopup id="scheduled-message" open onClose={onClose} title="Schedule Message">
      <div className={styles.grid}>
        <TextArea
          id="schedule-sms-input"
          fixedRows={3}
          onTextChange={onTextChange}
          value={data.message}
          placeholder="SMS text"
          description={`Used characters: ${data.message.length}`}
          withError
          errorText={errors.message}
        />
        <DatePickerNew
          id="scheduled-time"
          header="Scheduled Time"
          errorText={errors.scheduledTime}
          minDate={new Date()}
          onDateSelected={(date) => onDateChange(date)}
          onBlur={(date) => onDateChange(date)}
          value={data.scheduledAt}
          withTime
          minuteIntervals={15}
          required
          disableManualInput
        />
        <p className="m-t-4">
          Note: Selected datetime will be according to Patient&apos;s timezone
        </p>
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit: sendSmsRequest,
        isSubmitEnabled: getIsButtonEnabled(data, errors),
        submitButtonText: 'Schedule and Close',
      })}
    </BasePopup>
  );
};

ScheduledMessagePopup.propTypes = {
  id: PropTypes.string,
  patientId: PropTypes.number,
  phoneNumber: PropTypes.string,
  onClose: PropTypes.func,
};

export default ScheduledMessagePopup;
